// frontend/src/components/NavBar.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield, faRightFromBracket, faCoins, faTrophy, faCampground, faBars, faList, faHouse, faGamepad } from '@fortawesome/free-solid-svg-icons';
import '../styles/NavBar.css';
import { useAuth } from '../auth/AuthProvider';  // ⬅️ add this
import { useNotifications, useAnyAttention } from '../notifications/NotificationsContext';
import AttentionBadge from './AttentionBadge';
import WhatsNewModal from './WhatsNewModal';
import { getRelease } from '../whatsnew/releases';

import { apiFetch } from '../api/client';

export default function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, token, isAuthenticated, loading } = useAuth();                  // ⬅️ pull from context
  const [campaigns, setCampaigns] = useState([]);
  const [showHomeMenu, setShowHomeMenu] = useState(false);
  const [showShopMenu, setShowShopMenu] = useState(false);
  const [showPlayMenu, setShowPlayMenu] = useState(false);
  const [showLeaderboardMenu, setShowLeaderboardMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [completionByCampaign, setCompletionByCampaign] = useState({});
  const homeMenuRef = useRef(null);
  const shopMenuRef = useRef(null);
  const playMenuRef = useRef(null);
  const leaderboardMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const [bottomMenu, setBottomMenu] = useState(null);
  const bottomNavRef = useRef(null);

  const isActive = (pattern) => {
    if (pattern instanceof RegExp) return pattern.test(location.pathname);
    return location.pathname.startsWith(pattern);
  };
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isCampaignDashboard = pathParts[0] === 'campaign' && pathParts.length === 2;
  const singleCampaign = campaigns.length === 1 ? campaigns[0] : null;

  // Attention badge for the active campaign (refunds, rewards, claimable streak).
  const activeCampaignId = useMemo(() => {
    if ((pathParts[0] === 'campaign' || pathParts[0] === 'leaderboard') && pathParts[1]) {
      return Number(pathParts[1]);
    }
    if (location.pathname.startsWith('/game')) {
      const q = new URLSearchParams(location.search).get('campaign_id');
      return q ? Number(q) : null;
    }
    return null;
  }, [pathParts, location.pathname, location.search]);
  const { refresh: refreshNotifications, refreshSummary, byCampaign } = useNotifications();
  // Main Basecamp button: lit if ANY of the user's campaigns needs attention.
  const anyAttention = useAnyAttention();
  const campaignHasAttention = (cid) => {
    const a = byCampaign[cid];
    return Boolean(a && ((a.unseenCount || 0) > 0 || (a.accoladeCount || 0) > 0 || (a.streakClaimableCoins || 0) > 0));
  };
  useEffect(() => {
    refreshSummary();
  }, [location.pathname, refreshSummary]);
  useEffect(() => {
    if (activeCampaignId) refreshNotifications(activeCampaignId);
  }, [activeCampaignId, location.pathname, refreshNotifications]);

  // "What's New" release badge next to the B4W brand.
  const [whatsNew, setWhatsNew] = useState({ release_key: null, has_unseen: false });
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const fetchWhatsNew = useCallback(async () => {
    if (loading || !isAuthenticated || !token) return;
    try {
      const res = await apiFetch(`/api/updates/whats_new`, {
        headers: {},
      });
      if (!res.ok) return;
      const data = await res.json();
      setWhatsNew({ release_key: data?.release_key || null, has_unseen: Boolean(data?.has_unseen) });
    } catch {
      /* best-effort */
    }
  }, [loading, isAuthenticated, token]);
  useEffect(() => {
    fetchWhatsNew();
  }, [fetchWhatsNew, location.pathname]);
  const openWhatsNew = async () => {
    setShowWhatsNew(true);
    if (whatsNew.has_unseen) {
      setWhatsNew((prev) => ({ ...prev, has_unseen: false }));
      try {
        await apiFetch(`/api/updates/whats_new/seen`, {
          method: 'POST',
          headers: {},
        });
      } catch {
        /* best-effort */
      }
    }
  };

  const homeActive = isActive('/home');
  const basecampActive = isCampaignDashboard;
  const marketActive = location.pathname.includes('/market') || location.pathname.includes('/shop');
  const playActive = isActive('/game');
  const standingsActive = isActive('/leaderboard');
  const bottomTargets = {
    play: (c) => `/game?campaign_id=${c.campaign_id}`,
    basecamp: (c) => `/campaign/${c.campaign_id}`,
    market: (c) => `/campaign/${c.campaign_id}/market`,
    standings: (c) => `/leaderboard/${c.campaign_id}`,
  };
  const handleBottom = (kind) => {
    if (singleCampaign) {
      navigate(bottomTargets[kind](singleCampaign));
      return;
    }
    setBottomMenu((prev) => (prev === kind ? null : kind));
  };

  const handleLogout = async () => {
    try {
      await apiFetch(`/api/logout`, { method: 'POST', credentials: 'include' });
    } catch (_) {
      // ignore network errors
    } finally {
      // 🔑 this updates token/user + localStorage + navigates to /login
      logout();
    }
  };

  const fetchCampaigns = useCallback(async () => {
    if (loading || !isAuthenticated || !token) return;
    try {
      const res = await apiFetch(`/api/user/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
      });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setCampaigns(list);
      const completion = {};
      list.forEach((camp) => {
        completion[camp.campaign_id] = camp.daily_completed === 1 || camp.daily_completed === true;
      });
      setCompletionByCampaign(completion);
    } catch {
      setCampaigns([]);
      setCompletionByCampaign({});
    }
  }, [loading, isAuthenticated, token]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns, location.pathname]);

  useEffect(() => {
    if (showPlayMenu || showHomeMenu || showShopMenu || showLeaderboardMenu || bottomMenu) {
      fetchCampaigns();
    }
  }, [showPlayMenu, showHomeMenu, showShopMenu, showLeaderboardMenu, bottomMenu, fetchCampaigns]);

  // close the bottom-nav picker on route change
  useEffect(() => {
    setBottomMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e) => {
      if (homeMenuRef.current && !homeMenuRef.current.contains(e.target)) {
        setShowHomeMenu(false);
      }
      if (shopMenuRef.current && !shopMenuRef.current.contains(e.target)) {
        setShowShopMenu(false);
      }
      if (playMenuRef.current && !playMenuRef.current.contains(e.target)) {
        setShowPlayMenu(false);
      }
      if (leaderboardMenuRef.current && !leaderboardMenuRef.current.contains(e.target)) {
        setShowLeaderboardMenu(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
      if (bottomNavRef.current && !bottomNavRef.current.contains(e.target)) {
        setBottomMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <>
    <nav className="nav-bar">
      <div className="nav-inner">
        <div className="brand-wrap">
          <button
            className={`brand ${isActive('/home') ? 'brand-glow' : ''}`}
            aria-label="Home"
            title="Home"
            onClick={() => navigate('/home')}
          >
            B4W
          </button>
          {whatsNew.release_key && (
            <button
              className="nav-whatsnew"
              type="button"
              onClick={openWhatsNew}
              aria-label="What's New"
              title="What's New"
            >
              <span aria-hidden="true">✨</span>
              {whatsNew.has_unseen && <AttentionBadge title="See what's new" />}
            </button>
          )}
        </div>

        <div className="nav-center">
          <div className="nav-dropdown" ref={homeMenuRef}>
            <button
              className={`icon-btn nav-primary nav-pill ${isCampaignDashboard ? 'active' : ''} ${showHomeMenu ? 'menu-open' : ''}`}
              aria-label="Basecamp"
              title="Basecamp"
              onClick={() => {
                if (singleCampaign) {
                  navigate(`/campaign/${singleCampaign.campaign_id}`);
                  return;
                }
                setShowHomeMenu((prev) => !prev);
              }}
            >
              <span className="attention-anchor">
                <FontAwesomeIcon icon={faCampground} />
                {anyAttention && <AttentionBadge title="A campaign needs your attention" />}
              </span>
              <span className="nav-label">Basecamp</span>
            </button>
            {showHomeMenu && (
              <div className="nav-dropdown-menu nav-dropdown-menu-left">
                {campaigns.length === 0 ? (
                  <div className="nav-dropdown-empty">No campaigns</div>
                ) : (
                  campaigns.map((camp) => (
                    <button
                      key={camp.campaign_id}
                      className={`nav-dropdown-item${camp.is_admin_campaign ? ' admin-campaign' : ''}`}
                      type="button"
                      onClick={() => {
                        setShowHomeMenu(false);
                        navigate(`/campaign/${camp.campaign_id}`);
                      }}
                    >
                      {camp.name}
                      {campaignHasAttention(camp.campaign_id) && (
                        <AttentionBadge title={`${camp.name} needs your attention`} />
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="nav-dropdown" ref={shopMenuRef}>
            <button
              className={`icon-btn nav-primary nav-pill ${isActive('/market') || location.pathname.includes('/market') || location.pathname.includes('/shop') ? 'active' : ''} ${showShopMenu ? 'menu-open' : ''}`}
              aria-label="Market"
              title="Market"
              onClick={() => {
                if (singleCampaign) {
                  navigate(`/campaign/${singleCampaign.campaign_id}/market`);
                  return;
                }
                setShowShopMenu((prev) => !prev);
              }}
            >
              <FontAwesomeIcon icon={faCoins} />
              <span className="nav-label">Market</span>
            </button>
            {showShopMenu && (
              <div className="nav-dropdown-menu">
                {campaigns.length === 0 ? (
                  <div className="nav-dropdown-empty">No campaigns</div>
                ) : (
                  campaigns.map((camp) => (
                    <button
                      key={camp.campaign_id}
                      className={`nav-dropdown-item${camp.is_admin_campaign ? ' admin-campaign' : ''}`}
                      type="button"
                      onClick={() => {
                        setShowShopMenu(false);
                        navigate(`/campaign/${camp.campaign_id}/market`);
                      }}
                    >
                      {camp.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="nav-dropdown" ref={playMenuRef}>
            <button
              className={`icon-btn nav-primary nav-pill ${isActive('/game') ? 'active' : ''} ${showPlayMenu ? 'menu-open' : ''}`}
              aria-label="Play"
              title="Play"
              onClick={() => {
                if (singleCampaign) {
                  navigate(`/game?campaign_id=${singleCampaign.campaign_id}`);
                  return;
                }
                setShowPlayMenu((prev) => !prev);
              }}
            >
              <FontAwesomeIcon icon={faGamepad} />
              <span className="nav-label">Play</span>
            </button>
            {showPlayMenu && (
              <div className="nav-dropdown-menu">
                {campaigns.length === 0 ? (
                  <div className="nav-dropdown-empty">No campaigns</div>
                ) : (
                  campaigns.map((camp) => (
                    <button
                      key={camp.campaign_id}
                      className={`nav-dropdown-item${camp.is_admin_campaign ? ' admin-campaign' : ''}`}
                      type="button"
                      onClick={() => {
                        setShowPlayMenu(false);
                        navigate(`/game?campaign_id=${camp.campaign_id}`);
                      }}
                    >
                      <span className="nav-dropdown-label">{camp.name}</span>
                      <span className={`nav-dropdown-status ${completionByCampaign[camp.campaign_id] ? 'done' : 'todo'}`}>
                        {completionByCampaign[camp.campaign_id] ? '✅' : '❌'}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="nav-dropdown" ref={leaderboardMenuRef}>
            <button
              className={`icon-btn nav-primary nav-pill ${isActive('/leaderboard') ? 'active' : ''} ${showLeaderboardMenu ? 'menu-open' : ''}`}
              aria-label="Leaderboard"
              title="Leaderboard"
              onClick={() => {
                if (singleCampaign) {
                  navigate(`/leaderboard/${singleCampaign.campaign_id}`);
                  return;
                }
                setShowLeaderboardMenu((prev) => !prev);
              }}
            >
              <FontAwesomeIcon icon={faTrophy} />
              <span className="nav-label">Standings</span>
            </button>
            {showLeaderboardMenu && (
              <div className="nav-dropdown-menu">
                {campaigns.length === 0 ? (
                  <div className="nav-dropdown-empty">No campaigns</div>
                ) : (
                  campaigns.map((camp) => (
                    <button
                      key={camp.campaign_id}
                      className={`nav-dropdown-item${camp.is_admin_campaign ? ' admin-campaign' : ''}`}
                      type="button"
                      onClick={() => {
                        setShowLeaderboardMenu(false);
                        navigate(`/leaderboard/${camp.campaign_id}`);
                      }}
                    >
                      {camp.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

        </div>

        <div className="nav-right">
          <div className="nav-dropdown" ref={userMenuRef}>
            <button
              className={`icon-btn ${isActive('/campaigns') || isActive('/account') || isActive('/updates') ? 'active' : ''} ${showUserMenu ? 'menu-open' : ''}`}
              aria-label="Menu"
              title="Menu"
              onClick={() => setShowUserMenu((prev) => !prev)}
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
            {showUserMenu && (
              <div className="nav-dropdown-menu">
                <button
                  className="nav-dropdown-item"
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/home');
                  }}
                >
                  <FontAwesomeIcon icon={faHouse} /> Home
                </button>
                <button
                  className="nav-dropdown-item"
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/account');
                  }}
                >
                  <FontAwesomeIcon icon={faUserShield} /> Account
                </button>
                <button
                  className="nav-dropdown-item"
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/updates');
                  }}
                >
                  <FontAwesomeIcon icon={faList} /> Update Log
                </button>
                <button
                  className="nav-dropdown-item danger"
                  type="button"
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                >
                  <FontAwesomeIcon icon={faRightFromBracket} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>

    {/* Mobile bottom tab bar (hidden on desktop via CSS) */}
    <nav className="bottom-nav" ref={bottomNavRef} aria-label="Primary">
        {bottomMenu && (
          <div className="bnav-sheet">
            <div className="bnav-sheet-title">
              {bottomMenu === 'basecamp'
                ? 'Choose a Basecamp'
                : bottomMenu === 'market'
                ? 'Choose a Market'
                : bottomMenu === 'play'
                ? 'Choose a Campaign to Play'
                : 'Choose Standings'}
            </div>
            {campaigns.length === 0 ? (
              <div className="nav-dropdown-empty">No campaigns</div>
            ) : (
              campaigns.map((camp) => (
                <button
                  key={camp.campaign_id}
                  className={`nav-dropdown-item${camp.is_admin_campaign ? ' admin-campaign' : ''}`}
                  type="button"
                  onClick={() => {
                    setBottomMenu(null);
                    navigate(bottomTargets[bottomMenu](camp));
                  }}
                >
                  <span className="nav-dropdown-label">{camp.name}</span>
                  {bottomMenu === 'play' && (
                    <span className={`nav-dropdown-status ${completionByCampaign[camp.campaign_id] ? 'done' : 'todo'}`}>
                      {completionByCampaign[camp.campaign_id] ? '✅' : '❌'}
                    </span>
                  )}
                  {bottomMenu === 'basecamp' && campaignHasAttention(camp.campaign_id) && (
                    <AttentionBadge title={`${camp.name} needs your attention`} />
                  )}
                </button>
              ))
            )}
          </div>
        )}

        <button
          className={`bnav-item ${homeActive ? 'active' : ''}`}
          type="button"
          onClick={() => { setBottomMenu(null); navigate('/home'); }}
        >
          <FontAwesomeIcon icon={faHouse} />
          <span className="bnav-label">Home</span>
        </button>
        <button
          className={`bnav-item ${basecampActive ? 'active' : ''} ${bottomMenu === 'basecamp' ? 'open' : ''}`}
          type="button"
          onClick={() => handleBottom('basecamp')}
        >
          <span className="attention-anchor">
            <FontAwesomeIcon icon={faCampground} />
            {anyAttention && <AttentionBadge title="A campaign needs your attention" />}
          </span>
          <span className="bnav-label">Basecamp</span>
        </button>
        <button
          className={`bnav-center ${playActive ? 'active' : ''} ${bottomMenu === 'play' ? 'open' : ''}`}
          type="button"
          onClick={() => handleBottom('play')}
          aria-label="Play"
        >
          <span className="bnav-center-badge"><span className="bnav-center-emoji" aria-hidden="true">⚔️</span></span>
          <span className="bnav-center-label">Play</span>
        </button>
        <button
          className={`bnav-item ${marketActive ? 'active' : ''} ${bottomMenu === 'market' ? 'open' : ''}`}
          type="button"
          onClick={() => handleBottom('market')}
        >
          <FontAwesomeIcon icon={faCoins} />
          <span className="bnav-label">Market</span>
        </button>
        <button
          className={`bnav-item ${standingsActive ? 'active' : ''} ${bottomMenu === 'standings' ? 'open' : ''}`}
          type="button"
          onClick={() => handleBottom('standings')}
        >
          <FontAwesomeIcon icon={faTrophy} />
          <span className="bnav-label">Standings</span>
        </button>
      </nav>
      <WhatsNewModal
        visible={showWhatsNew}
        release={getRelease(whatsNew.release_key)}
        onClose={() => setShowWhatsNew(false)}
      />
    </>
  );
}
